import { isLatinoChannel } from '@/utils/common/latino-app';

/** DataGrail embed consent-loader (pairs with GTM per channel/env in channel customize variables). */
const DATAGRAIL_CONSENT_BASE = 'https://api.consentjs.datagrail.io/e0e1568a-2ef6-4022-8f3d-487fd487e34f';

export const DATAGRAIL_STYLESHEET_MAIN = 'https://click.sayweee.com/static/datagrail-styles-main.css';
export const DATAGRAIL_STYLESHEET_LATINO = 'https://click.sayweee.com/static/datagrail-styles-latino.css';

const DATAGRAIL_CONSENT_APP_IDS = {
  /** tb1.sayweee.net — GTM-596KDHX */
  weeeTest: '4c100f5d-8601-4c72-acba-b13f9c431508',
  /** www.sayweee.com — GTM-KTQGLRG */
  weeeProd: '32b4f34e-d5b4-4dc6-8660-982bf950b25c',
  /** www.masgusto.com — GTM-T3WJBLJ3 */
  masgustoProd: '0a6e0052-439a-4e4d-a019-2766a80c7371',
  /** tb1.masgusto.net — GTM-M795JTGD */
  masgustoTest: 'e57121a0-6df3-431d-bd31-b543da82128e'
} as const;

export const getDatagrailConsentLoaderSrc = (isLatino: boolean) => {
  const isProd = (process.env.ENV_VAR || process.env.NEXT_PUBLIC_ENV_VAR) === 'prod';
  const appId = isLatino
    ? isProd
      ? DATAGRAIL_CONSENT_APP_IDS.masgustoProd
      : DATAGRAIL_CONSENT_APP_IDS.masgustoTest
    : isProd
    ? DATAGRAIL_CONSENT_APP_IDS.weeeProd
    : DATAGRAIL_CONSENT_APP_IDS.weeeTest;

  return `${DATAGRAIL_CONSENT_BASE}/${appId}/consent-loader.js`;
};

/** Current channel’s consent-loader URL (Weee vs MasGusto). */
export const getCurrentDatagrailConsentLoaderSrc = () => getDatagrailConsentLoaderSrc(isLatinoChannel());

export const DATAGRAIL_PRIVACY_CHOICES_LAYER_ID = 'categories-layer';
export const DATAGRAIL_BANNER_API_POLL_MS = 300;
export const DATAGRAIL_BANNER_API_MAX_WAIT_MS = 15000;

/** Resolves DataGrail stylesheet URL for the current Weee / Mas Gusto channel. */
export const getDatagrailStylesheetHref = (isLatino: boolean): string =>
  isLatino ? DATAGRAIL_STYLESHEET_LATINO : DATAGRAIL_STYLESHEET_MAIN;

/**
 * Returns an inline script that injects DataGrail style link into the banner shadow root.
 * DataGrail banner styles use `:host(...)`, so page-level stylesheets won't match.
 */
export const getDatagrailShadowStylesInjectorInlineScript = (stylesheetHref: string): string => {
  return `(() => {
  const stylesheetHref = ${JSON.stringify(stylesheetHref)};
  const hostSelector = 'aside.dg-consent-banner';
  const injectedAttr = 'data-weee-datagrail-style';
  const observeTimeoutMs = 15000;

  const appendStyleLink = (shadowRoot) => {
    if (!shadowRoot || shadowRoot.querySelector('link[' + injectedAttr + '="1"]')) {
      return true;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = stylesheetHref;
    link.setAttribute(injectedAttr, '1');
    shadowRoot.appendChild(link);
    return true;
  };

  const tryInject = () => {
    const host = document.querySelector(hostSelector);
    if (!host || !host.shadowRoot) return false;
    return appendStyleLink(host.shadowRoot);
  };

  if (tryInject()) return;

  const observer = new MutationObserver(() => {
    if (!tryInject()) return;
    observer.disconnect();
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
  window.setTimeout(() => observer.disconnect(), observeTimeoutMs);
})();`;
};

/** Bridges fully granted OneTrust consent cookies to DataGrail for existing web users. */
export const getOneTrustToDatagrailBridgeInlineScript = (): string => {
  return `(() => {
  const hideStyleId = 'datagrail-bridge-hide-style';

  function removeHideStyle() {
    const hideStyle = document.getElementById(hideStyleId);
    if (hideStyle && hideStyle.parentNode) {
      hideStyle.parentNode.removeChild(hideStyle);
    }
  }

  function getCookieValue(name) {
    const prefix = name + '=';
    const cookies = (window.document.cookie || '').split(';');
    for (let i = 0; i < cookies.length; i += 1) {
      const cookie = cookies[i].trim();
      if (cookie.indexOf(prefix) === 0) {
        return cookie.slice(prefix.length);
      }
    }
    return '';
  }

  function parseOptanonCookie() {
    const rawCookie = getCookieValue('OptanonConsent');
    if (!rawCookie) return undefined;

    let decodedCookie = rawCookie;
    try {
      decodedCookie = decodeURIComponent(rawCookie);
    } catch (e) {}

    const groupsMatch = decodedCookie.match(/(?:^|&)groups=([^&]+)/);
    const groupString = groupsMatch ? groupsMatch[1] : decodedCookie;
    const groups = {};

    groupString.split(',').forEach(function(groupPair) {
      const parts = groupPair.split(':');
      if (parts.length !== 2) return;

      groups[parts[0]] = parts[1] === '1';
    });

    return groups;
  }

  function alreadyHasDatagrailConsent() {
    let documentCookieFromDG;
    try {
      documentCookieFromDG = window.document.cookie.split('datagrail_consent_id=')[1].split(';')[0];
    } catch (e) {}

    return !!documentCookieFromDG;
  }

  function isFullyGranted(groups) {
    if (!groups) return false;

    return groups.C0001 === true && groups.C0002 === true && groups.C0003 === true && groups.C0004 === true;
  }

  function getFullyGrantedCookieOptions(cookieOptions) {
    if (!Array.isArray(cookieOptions)) return cookieOptions;

    return cookieOptions.map(function(option) {
      return Object.assign({}, option, { isEnabled: true });
    });
  }

  const otGroups = parseOptanonCookie();

  if (alreadyHasDatagrailConsent() || !isFullyGranted(otGroups)) {
    removeHideStyle();
    console.log('[OT->DG] Not fully granted, already migrated, or no OT cookie. Banner will show normally.');
  }

  window.dgEvent = window.dgEvent || [];
  window.DG_CONSENT_BLOCK_RELOAD = true;
  window.dgEvent.push({ event: 'block_reload', block: true });
  window.dgEvent.push({
    event: 'initial_preference_callback',
    params: function(bannerApi) {
      console.log('[OT->DG] initial_preference_callback fired. Checking consent migration.');
      try {
        const currentPrefs =
          bannerApi && typeof bannerApi.getConsentPreferences === 'function'
            ? bannerApi.getConsentPreferences()
            : undefined;

        if (currentPrefs && currentPrefs.cookieOptions) {
          try {
            window.__consentOnPageLand = JSON.stringify(currentPrefs.cookieOptions);
          } catch (e) {}
        }

        if (alreadyHasDatagrailConsent() || !isFullyGranted(otGroups)) {
          console.log('[OT->DG] Will not port consent.');
          return;
        }

        const nextPrefs = Object.assign({}, currentPrefs || {}, {
          cookieOptions: getFullyGrantedCookieOptions(currentPrefs && currentPrefs.cookieOptions),
          isCustomised: true
        });

        if (bannerApi && typeof bannerApi.setConsentPreferences === 'function') {
          bannerApi.setConsentPreferences(nextPrefs);
        } else if (bannerApi && typeof bannerApi.updateConsentPreferences === 'function') {
          bannerApi.updateConsentPreferences(nextPrefs);
        }
      } catch (e) {
        console.warn('[OT->DG] Failed to port consent.', e);
        throw e;
      } finally {
        removeHideStyle();
      }
    }
  });
  window.dgEvent.push({
    event: 'preference_callback',
    params: function(preferences) {
      if (preferences && preferences.consentPreferences && preferences.consentPreferences.cookieOptions) {
        const toCompare = JSON.stringify(preferences.consentPreferences.cookieOptions);
        let initialConsent;
        try {
          initialConsent = window.__consentOnPageLand;

          const didConsentChange = toCompare !== initialConsent;
          if (didConsentChange && initialConsent && toCompare) {
            const initialPermissions = initialConsent.split('true').length - 1;
            const newPermissions = toCompare.split('true').length - 1;
            const isDowngraded = newPermissions < initialPermissions;

            if (isDowngraded) {
              console.log('[OT->DG] Consent downgraded. Unblocking reload.');
              const bannerApi = window.DG_BANNER_API;
              if (bannerApi && typeof bannerApi.blockReload === 'function') {
                bannerApi.blockReload(false);
              }
              if (window.DG_CONSENT_BLOCK_RELOAD) {
                delete window.DG_CONSENT_BLOCK_RELOAD;
              }
            }
          }
        } catch (e) {}
      }
    }
  });
})();`;
};
